import * as React from 'react';
import { escapeRegExp } from 'lodash';
import { WithChildrenCommonProps } from '@types';

export type HighlightProps = WithChildrenCommonProps<{
  /** Part of text to be highlighted */
  query?: string;
  /**
   * Function that will return an array of strings.
   * Those that match with the query are the ones highlighted.
   * By default matches start of word
   */
  searchFn?: (text: string, query?: string) => string[];
  /** String to highlight */
  children?: string;
}>;

export function defaultHighlightFn(text: string, query?: string): string[] {
  if (!query?.trim()) {
    return [text];
  }

  return text.split(new RegExp(`(\\s|^)(${escapeRegExp(query.trim())})`, 'gi'));
}

const HighlightComp = ({
  query,
  searchFn = defaultHighlightFn,
  children,
  ...rest
}: HighlightProps): JSX.Element | null => {
  const isNotString = typeof children !== 'string';
  if (!children || isNotString) {
    isNotString && console.warn('«Highlight» children must be an string');
    return null;
  }

  const splittedText = searchFn(children, query);

  return (
    <span {...rest}>
      {splittedText.map((text: string, index: number) => {
        const isMatch =
          text.toLocaleLowerCase().trim() === query?.toLocaleLowerCase().trim();

        return isMatch ? (
          <span className="underline text-gray-dark font-semibold" key={index}>
            {text}
          </span>
        ) : (
          text
        );
      })}
    </span>
  );
};

const Highlight = React.memo(HighlightComp);

export { Highlight };
